import { api } from './api';

const INSTALL_DISMISS_KEY = 'anidex.pwa.install-dismissed';
const PUSH_PROMPT_KEY = 'anidex.pwa.push-asked';
const SERVICE_WORKER_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredInstall: BeforeInstallPromptEvent | null = null;
let serviceWorkerUpdateMonitorStarted = false;
const installListeners = new Set<() => void>();

function emitInstall() {
  for (const listener of installListeners) listener();
}

export function isStandaloneDisplay(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone))
  );
}

export function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return iOS && /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export function isMobileViewport(): boolean {
  return window.matchMedia('(max-width: 900px)').matches || navigator.maxTouchPoints > 0;
}

export function canNativeInstall(): boolean {
  return deferredInstall !== null;
}

export function installDismissed(): boolean {
  try {
    return localStorage.getItem(INSTALL_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissInstallPrompt() {
  try {
    localStorage.setItem(INSTALL_DISMISS_KEY, '1');
  } catch {
    // Ignore quota / private mode.
  }
  emitInstall();
}

export function shouldShowInstallPrompt(): boolean {
  if (typeof window === 'undefined') return false;
  if (isStandaloneDisplay() || installDismissed()) return false;
  return canNativeInstall() || isIosSafari();
}

export function watchInstallAvailability(onChange: () => void): () => void {
  installListeners.add(onChange);
  return () => installListeners.delete(onChange);
}

export async function promptNativeInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredInstall) return 'unavailable';
  const event = deferredInstall;
  deferredInstall = null;
  emitInstall();
  await event.prompt();
  const choice = await event.userChoice;
  if (choice.outcome === 'dismissed') dismissInstallPrompt();
  return choice.outcome;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    if (!serviceWorkerUpdateMonitorStarted) {
      serviceWorkerUpdateMonitorStarted = true;
      let hasControlledPage = Boolean(navigator.serviceWorker.controller);
      let reloadingForUpdate = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hasControlledPage) {
          hasControlledPage = true;
          return;
        }
        if (reloadingForUpdate) return;
        reloadingForUpdate = true;
        window.location.reload();
      });

      const checkForUpdate = () => {
        void registration.update().catch((error: unknown) => {
          console.warn('service_worker_update_check_failed', error);
        });
      };
      checkForUpdate();
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
      window.setInterval(checkForUpdate, SERVICE_WORKER_UPDATE_INTERVAL_MS);
    }
    return registration;
  } catch (error) {
    console.warn('service_worker_register_failed', error);
    return null;
  }
}

export function listenForInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredInstall = event as BeforeInstallPromptEvent;
    emitInstall();
  });
  window.addEventListener('appinstalled', () => {
    deferredInstall = null;
    dismissInstallPrompt();
  });
}

function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output.buffer;
}

export async function pushCapability(): Promise<{
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  enabled: boolean;
}> {
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { supported: false, permission: 'unsupported', enabled: false };
  }
  const status = await api<{ enabled: boolean; publicKey: string | null }>('/push/vapid-key');
  return { supported: true, permission: Notification.permission, enabled: status.enabled };
}

export async function getPushStatus(): Promise<{ enabled: boolean; subscribed: boolean }> {
  return api('/push/status');
}

export async function enablePushNotifications(): Promise<'granted' | 'denied' | 'unavailable'> {
  const capability = await pushCapability();
  if (!capability.supported || !capability.enabled) return 'unavailable';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  const registration = (await navigator.serviceWorker.ready) || (await registerServiceWorker());
  if (!registration) return 'unavailable';

  const { publicKey } = await api<{ enabled: boolean; publicKey: string | null }>('/push/vapid-key');
  if (!publicKey) return 'unavailable';

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToArrayBuffer(publicKey),
    }));

  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return 'unavailable';

  await api('/push/subscribe', {
    method: 'POST',
    body: {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    },
  });

  try {
    localStorage.setItem(PUSH_PROMPT_KEY, '1');
  } catch {
    // Ignore.
  }
  return 'granted';
}

export async function disablePushNotifications(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  const endpoint = subscription?.endpoint;
  if (subscription) await subscription.unsubscribe();
  const query = endpoint ? `?endpoint=${encodeURIComponent(endpoint)}` : '';
  await api(`/push/subscribe${query}`, { method: 'DELETE' });
}

export function pushPromptedBefore(): boolean {
  try {
    return localStorage.getItem(PUSH_PROMPT_KEY) === '1';
  } catch {
    return false;
  }
}
